import { useCallback, useEffect, useState } from "react";
import { Play, X } from "lucide-react";
import { Reveal } from "@/components/motion/Motion";
import { API } from "@/constants/roles";

const ORIGIN = API.replace(/\/api$/, "");

/**
 * On-site video viewer for showcase references. Native controls give play/pause,
 * seek, volume and fullscreen; Escape / backdrop click / X all close it. The body
 * scroll is frozen at the exact position and restored on close. Playback stops on
 * close because the <video> unmounts. Only one viewer can be open (single state).
 */
export const ShowcaseViewer = ({ item, onClose }) => {
  useEffect(() => {
    // Freeze scrolling without moving the page: stop Lenis (homepage smooth scroll)
    // and lock the document overflow. Closing restores both — position never changes.
    const lenis = window.__lenis;
    lenis?.stop();
    const html = document.documentElement.style;
    const prevOverflow = html.overflow;
    html.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      html.overflow = prevOverflow;
      lenis?.start();
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return (
    <div
      data-testid="showcase-viewer"
      className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-md"
      onClick={onClose}
    >
      <div className="relative flex max-h-full flex-col items-center" onClick={(e) => e.stopPropagation()}>
        <button
          data-testid="showcase-viewer-close"
          onClick={onClose}
          aria-label="Close video"
          className="absolute -top-3 right-0 z-10 flex h-10 w-10 translate-y-[-100%] items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 sm:right-[-3rem] sm:translate-y-0"
        >
          <X className="h-5 w-5" />
        </button>
        <video
          data-testid="showcase-viewer-video"
          src={`${ORIGIN}${item.video_url}`}
          controls
          autoPlay
          playsInline
          className="max-h-[78vh] w-auto max-w-full rounded-2xl border border-white/10 bg-black shadow-2xl"
        />
        <p className="mt-3 text-center text-xs font-medium text-white/70">
          {item.title || "Quality reference"}
          {item.creator ? <span className="text-white/50"> · by {item.creator}</span> : null}
        </p>
        <p className="mt-1 text-center text-[10px] uppercase tracking-widest text-white/40">Reference example — shown to demonstrate the target standard</p>
      </div>
    </div>
  );
};

const pad = (n) => String(n).padStart(2, "0");

/**
 * The Standard — a continuously moving right-to-left infinite marquee of the
 * owner-curated reference videos. Pure CSS animation: page scroll has zero
 * effect on the track, the loop is seamless (duplicated sequence, uniform
 * padding instead of flex gap so -50% lands exactly on the seam), and the
 * section can never cause horizontal page overflow.
 */
export const ShowcaseStrip = () => {
  const [items, setItems] = useState(null);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    fetch(`${API}/showcase`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setItems(Array.isArray(data) ? data : []))
      .catch(() => setItems([]));
  }, []);

  const open = items?.find((i) => i.id === openId) || null;
  const closeViewer = useCallback(() => setOpenId(null), []);

  return (
    <section data-testid="showcase-strip" className="relative overflow-hidden py-24 sm:py-32">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <Reveal>
          <p className="font-mono-label text-[10px] font-bold uppercase tracking-[0.3em] text-cyan-600">The standard</p>
          <h2 className="mt-4 max-w-3xl font-display text-4xl font-extrabold leading-[1.02] tracking-tight text-slate-900 sm:text-5xl">
            The quality we're building for.
          </h2>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-600">
            A look at the animation, acting, camera work, environments and pacing that define the standard we're looking for.
          </p>
        </Reveal>
      </div>

      {items && items.length > 0 && (
        <div
          data-testid="showcase-marquee"
          className="showcase-viewport mt-14 overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_5%,black_95%,transparent)]"
        >
          <div
            className={`showcase-track py-4${open ? " paused" : ""}`}
            style={{ "--marquee-duration": `${items.length * 9}s` }}
          >
            {[...items, ...items].map((s, i) => (
              <div key={`${s.id}-${i}`} className="shrink-0 pr-7">
                <button
                  data-testid={`showcase-shot-${i % items.length}`}
                  onClick={() => setOpenId(s.id)}
                  className="group relative block w-56 overflow-hidden rounded-3xl border border-white/10 bg-slate-900 text-left shadow-lg shadow-slate-900/15 transition-all duration-300 hover:-translate-y-2 hover:scale-[1.03] hover:border-cyan-400/40 hover:shadow-2xl hover:shadow-cyan-500/20 sm:w-72 xl:w-80"
                  aria-label={`Play ${s.title || `quality reference ${pad((i % items.length) + 1)}`}`}
                >
                  {/* fixed 3:4 portrait frame — media fills it with object-cover (crop, never stretch) */}
                  {s.thumb_url ? (
                    <img src={`${ORIGIN}${s.thumb_url}`} alt={s.title || "Quality reference"} loading="lazy" className="aspect-[3/4] w-full object-cover" />
                  ) : (
                    <video src={`${ORIGIN}${s.video_url}`} muted playsInline preload="metadata" className="aspect-[3/4] w-full object-cover" />
                  )}
                  {/* always-on play chip — cards read as playable at a glance */}
                  <span className="absolute bottom-3 right-3 flex h-9 w-9 items-center justify-center rounded-full border border-white/25 bg-black/45 text-white backdrop-blur-md transition-transform duration-300 group-hover:scale-110">
                    <Play className="ml-0.5 h-4 w-4" />
                  </span>
                  {/* hover center play */}
                  <span className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                    <span className="flex h-14 w-14 items-center justify-center rounded-full bg-cyan-500 text-white shadow-xl shadow-cyan-500/40">
                      <Play className="ml-1 h-6 w-6" />
                    </span>
                  </span>
                  {/* legibility gradient + secondary title/creator */}
                  <span className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/35 to-transparent px-4 pb-3.5 pt-14">
                    <span className="block truncate text-[13px] font-bold text-white">
                      {s.title || `Quality reference ${pad((i % items.length) + 1)}`}
                    </span>
                    <span className="mt-0.5 block truncate font-mono-label text-[9px] font-bold uppercase tracking-widest text-white/60">
                      {s.creator ? `by ${s.creator}` : "Quality reference"}
                    </span>
                  </span>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {open && <ShowcaseViewer item={open} onClose={closeViewer} />}
    </section>
  );
};
